export type MessageRole = 'user' | 'assistant';

export type MessageStatus = 'pending' | 'confirmed' | 'error' | 'streaming';

export type MessageIdentifier = string | number;

export interface ChatMessage {
  id: MessageIdentifier;
  role: MessageRole;
  content: string;
  createdAt?: string;
  created_at?: string;
  status?: MessageStatus;
  tempId?: string;
  isTyping?: boolean;
  chatId?: MessageIdentifier;
  chat_id?: MessageIdentifier;
  [key: string]: unknown;
}

import type { JournalEntry } from './journal';

export interface ChatSummary {
  id: MessageIdentifier;
  title?: string | null;
  patientId?: MessageIdentifier;
  patient_id?: MessageIdentifier;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  expiresAt?: string | null;
  expires_at?: string | null;
  expired?: boolean;
  expires_today?: boolean;
  expiresToday?: boolean;
  chatType?: string;
  chat_type?: string;
  prompt?: string | null;
  summary?: string | null;
  messages?: ChatMessage[];
  dailyJournalEntry?: JournalEntry | null;
  private?: boolean;
  can_view_messages?: boolean;
  canViewMessages?: boolean;
  // Safety monitoring fields
  risk_flag_level?: 'none' | 'warning' | 'critical';
  risk_flag_sources?: string[];
  risk_blocked?: boolean;
  risk_blocked_at?: string | null;
  content_hidden?: boolean;
  safety_block_reason?: string | null;
  [key: string]: unknown;
}

export const resolveMessageTimestamp = (message: ChatMessage): string =>
  message.createdAt ?? message.created_at ?? '';

export const normalizeChatMessage = (message: ChatMessage): ChatMessage => ({
  ...message,
  createdAt: message.createdAt ?? message.created_at,
  chatId: message.chatId ?? message.chat_id,
});

export const normalizeChatMessages = (messages: ChatMessage[] = []): ChatMessage[] =>
  messages.map(normalizeChatMessage);

export const normalizeChatSummary = (chat: ChatSummary): ChatSummary => ({
  ...chat,
  createdAt: chat.createdAt ?? chat.created_at,
  updatedAt: chat.updatedAt ?? chat.updated_at,
  expiresAt: chat.expiresAt ?? chat.expires_at,
  expired: typeof chat.expired === 'boolean' ? chat.expired : undefined,
  expiresToday: typeof chat.expiresToday === 'boolean' ? chat.expiresToday : chat.expires_today,
  patientId: chat.patientId ?? chat.patient_id,
  chatType: chat.chatType ?? chat.chat_type,
  messages: normalizeChatMessages(chat.messages),
  canViewMessages: chat.canViewMessages ?? chat.can_view_messages,
  private: chat.private ?? (typeof chat['private'] === 'boolean' ? (chat['private'] as boolean) : undefined),
});
